import { HTMLAttributes, PropsWithChildren, forwardRef, useMemo } from "react";
import { cx } from "@jugl-web/utils";
import { LoadingAnimation, LoadingAnimationProps } from "../LoadingAnimation";
import { InteractiveContainer } from "../../InteractiveContainer";
import { EmptyListContent, EmptyListContentProps } from "../EmptyListContent";

export type DataLoadingWrapperProps = HTMLAttributes<HTMLDivElement> &
  PropsWithChildren<{
    isLoading?: boolean;
    onRetry?: () => void;
    isError?: boolean;
    isFetching?: boolean;
    errorMessage?: string;
    animationSize?: LoadingAnimationProps["size"];
    customErrorContentProps?: EmptyListContentProps;
    isLoadingMore?: boolean;
    isLoadingMoreError?: boolean;
    loadingMoreErrorMessage?: string;
    onRetryLoadMore?: () => void;
  }>;

export const DataLoadingWrapper = forwardRef<
  HTMLDivElement,
  DataLoadingWrapperProps
>(
  (
    {
      children,
      isLoading,
      isError,
      isFetching,
      errorMessage,
      onRetry,
      className,
      animationSize,
      customErrorContentProps,
      isLoadingMore,
      isLoadingMoreError,
      loadingMoreErrorMessage,
      onRetryLoadMore,
      ...nativeProps
    },
    ref
  ) => {
    const defaultErrorMessage = "Can't load data";
    const defaultLoadingMoreErrorMessage = "Can't load more data";

    const $componentContent = useMemo(() => {
      if (isLoading && !isLoadingMore) {
        return <LoadingAnimation size={animationSize || "lg"} />;
      }
      if (isError) {
        if (customErrorContentProps) {
          return (
            <div>
              <EmptyListContent
                {...{ onRefetch: onRetry, ...customErrorContentProps }}
              />
            </div>
          );
        }
        return (
          <div className="text-grey-800 text-center text-sm">
            {errorMessage || defaultErrorMessage}
            <br />
            {!!onRetry && (
              <InteractiveContainer
                as="span"
                className="text-primary-500 font-medium"
                onClick={onRetry}
              >
                Click to retry
              </InteractiveContainer>
            )}
          </div>
        );
      }
      return null;
    }, [
      animationSize,
      customErrorContentProps,
      errorMessage,
      isError,
      isLoading,
      isLoadingMore,
      onRetry,
    ]);

    if (!$componentContent) {
      return (
        <>
          {children}
          {!isError && isLoadingMore && (
            <div className="flex w-full items-center justify-center px-4 py-4">
              <LoadingAnimation size={animationSize || "lg"} />
            </div>
          )}
          {!isError && isLoadingMoreError && (
            <div className="text-grey-800 px-4 py-4 text-center text-sm">
              {loadingMoreErrorMessage || defaultLoadingMoreErrorMessage}
              <br />
              {!!onRetryLoadMore && (
                <InteractiveContainer
                  as="span"
                  className="text-primary-500 font-medium"
                  onClick={onRetryLoadMore}
                >
                  Click to retry
                </InteractiveContainer>
              )}
            </div>
          )}
        </>
      );
    }

    return (
      <div
        ref={ref}
        {...{
          ...nativeProps,
          className: cx(
            "h-full",
            {
              "flex justify-center items-center px-4 py-8 w-full":
                isLoading || isError,
            },
            {
              relative: isFetching && !isLoading,
            },
            className
          ),
        }}
      >
        {$componentContent || children}
        {isFetching && !isLoading ? (
          <div className="animate-fade-in absolute top-0 left-0 right-0 bottom-0 z-[2005] flex items-center justify-center bg-white bg-opacity-50 backdrop-blur-[0.5px]">
            <LoadingAnimation size={animationSize || "lg"} />
          </div>
        ) : null}
      </div>
    );
  }
);
